import { useCallback } from 'react';
import styled from 'styled-components';
import { border } from 'styled-system';

import { Avatar } from '@hivebrite/design-system/Avatar';
import { Box } from '@hivebrite/design-system/Box';
import {
  CATEGORIES,
  FontAwesomeIcon,
} from '@hivebrite/design-system/FontAwesomeIcon';
import { useTheme } from '@hivebrite/design-system/Theme';

import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import { completeName } from 'helpers/name';

import {
  type UserAvatarProps,
  type UserControlProps,
  type UserItemProps,
  type UserRowProps,
} from './types';

export const UserItem = ({
  cantSelect,
  checked,
  handleSelectUserClick,
  isFrozen,
  type,
  user,
}: UserItemProps) => {
  const isSelected = type === 'selected';
  const theme = useTheme();

  const { flagEvUserPicturesToGlobalStorage } = useFeatureFlag();

  const handleClick = useCallback(() => {
    if (!isFrozen) {
      handleSelectUserClick?.(user);
    }
  }, [handleSelectUserClick, isFrozen, user]);

  return (
    <UserRow
      $isSelectable={(!cantSelect && !isSelected && !isFrozen) || isSelected}
      data-testid="user-targeting-user"
      onClick={handleClick}
    >
      <UserAvatar
        $isSelected={isSelected}
        // @ts-expect-error will be fixed
        src={
          flagEvUserPicturesToGlobalStorage
            ? user.new_photo?.thumbnail_url
            : user.photo_thumb_url
        }
      />
      <UserName>{completeName(user)}</UserName>
      {isFrozen ? null : (
        <UserControl
          $isChecked={checked}
          $isSelected={isSelected}
          $theme={theme}
          aria-checked={checked}
          role="checkbox"
        >
          <FontAwesomeIcon
            height="16px"
            iconName={isSelected ? 'xmark' : 'check'}
            iconType={CATEGORIES.SOLID}
          />
        </UserControl>
      )}
    </UserRow>
  );
};

export default UserItem;

const UserRow = styled(Box).attrs<UserRowProps>({
  display: 'grid',
  gridTemplateColumns: 'min-content 1fr min-content',
  m: 2,
  maxWidth: '100%',
})<UserRowProps>`
  cursor: ${({ $isSelectable }) => ($isSelectable ? 'pointer' : 'default')};
`;

const UserAvatar = styled(Avatar).attrs<UserAvatarProps>(({ $isSelected }) => ({
  alignSelf: 'center',
  justifySelf: 'center',
  mx: 2,
  my: 1,
  minHeight: $isSelected ? 32 : 40,
  minWidth: $isSelected ? 32 : 40,
  size: $isSelected ? 32 : 40,
}))<UserAvatarProps>``;

const UserName = styled(Box).attrs({
  alignSelf: 'center',
  display: 'block',
  m: 1,
  overflow: 'hidden',
})`
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UserControl = styled(Box).attrs<UserControlProps>(
  ({ $isChecked, $isSelected, $theme }) => ({
    alignSelf: 'center',
    bg: $isChecked
      ? $theme.checkbox.background.checked
      : $theme.checkbox.background.unchecked,
    borderColor: $theme.checkbox.border.color,
    borderStyle: 'solid',
    borderRadius: $theme.checkbox.border.radius,
    borderWidth: $isSelected ? 0 : 1,
    color: $isSelected
      ? $theme.checkbox.background.checked
      : $theme.colors.white,
    justifySelf: 'center',
    minHeight: 20,
    minWidth: 20,
    mx: 2,
    my: 1,
    pl: '2px',
    pt: '1px',
    size: 20,
  }),
)<UserControlProps>`
  ${border}
  user-select: none;
`;
